export type EncryptionSource = 'self' | 'folder'

export type PublicPlanKey = 'free' | 'pro' | 'team' | 'enterprise'
export type BillablePlanKey = 'pro' | 'team'
export type BillingInterval = 'month' | 'year'

export interface PublicPlan {
  key: PublicPlanKey
  name: string
  monthly: number | null
  annual: number | null
  description: string
  features: readonly string[]
  highlighted?: boolean
  perSeat?: boolean
  minSeats?: number
  contact?: boolean
}

export const TRIAL_DAYS = 30
export const TRIAL_PLAN_NAME = 'Pro'

/**
 * Title prefixes of notes the app creates on the user's behalf: signup seeds
 * the Welcome and "Beautiful Notes" guides, and the sample-meeting entry point
 * creates a "Sample:" note. None of them count as the user's own work.
 */
export const SEEDED_NOTE_TITLE_PREFIXES = ['Welcome to OmniCanvas', 'How to Make Beautiful Notes', 'Sample:'] as const

/**
 * The customer journey, in order. The server records the first time each one is
 * reached in `user_milestones` (migration 0039); the dashboard reads it to see
 * where users stall.
 */
export const USER_MILESTONES = ['signed_up', 'first_note', 'third_note', 'folder', 'pro_feature', 'paid'] as const
export type UserMilestone = (typeof USER_MILESTONES)[number]

/**
 * Canonical, user-visible product capabilities.
 *
 * Marketing comparisons and product FAQs should read these values instead of
 * maintaining their own copy. Keep this deliberately limited to capabilities
 * that are stable enough to describe outside the app.
 */
export const PRODUCT_CAPABILITIES = {
  applePencil: true,
  collaboration: true,
  maxCollaboratorsPerNote: 25,
  offline: true,
  onDeviceAI: true,
  platforms: ['mac', 'windows', 'web', 'ipad', 'android'],
} as const

export const PUBLIC_PLANS: readonly PublicPlan[] = [
  {
    key: 'free',
    name: 'Free',
    monthly: 0,
    annual: 0,
    description: 'A complete visual notebook, no strings attached',
    features: [
      'Unlimited notes and infinite canvases',
      'Rich text, drawing, images, and sticky notes',
      'Cloud sync across devices',
      'Folders, tags, wiki-links, and full-text search',
      'Graph, Kanban, Table, and Focus views',
      'Optional end-to-end encryption',
      '30 minutes of meeting transcription each month',
      '3 daily version snapshots',
    ],
  },
  {
    key: 'pro',
    name: 'Pro',
    monthly: 8,
    annual: 80,
    // Ordered by what people actually reach, not by what is most expensive to
    // run. Fourteen of seventeen recorded upgrade prompts come from
    // handwriting-to-text and none from meeting transcription, and version
    // history accrues in the background whether or not anyone opts in. Leading
    // on transcription sold the one capability four accounts have ever used.
    // Same entitlements as before -- only the order and framing changed.
    description: 'Handwriting to text, a year of history, and every AI tool',
    highlighted: true,
    features: [
      'Everything in Free',
      'Handwriting to editable text, converted on your device',
      '365 daily version snapshots — a year of recoverable history',
      'AI actions, custom prompts, and semantic search',
      'Document and business-card scanning (OCR)',
      'YouTube and web clipping with AI cleanup',
      'REST API and MCP server access',
      '10 hours of meeting transcription each month',
      'Live transcription and structured capture packages',
    ],
  },
  {
    key: 'team',
    name: 'Team',
    monthly: 12,
    annual: 120,
    description: 'A shared workspace with pooled usage and central billing',
    perSeat: true,
    minSeats: 3,
    features: [
      'Everything in Pro for every member',
      'Shared team workspace and folders',
      'Named members with admin controls',
      '50 pooled transcription hours each month',
      '50 GB pooled storage',
      'Central billing with one invoice',
    ],
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    monthly: null,
    annual: null,
    description: 'Procurement, compliance, and support for larger teams',
    contact: true,
    features: [
      'Everything in Team',
      'SSO and SAML',
      'Audit logs and custom data retention',
      'Invoicing and annual contracts',
      'Priority support',
    ],
  },
] as const

export function getPublicPlan(key: PublicPlanKey): PublicPlan {
  return PUBLIC_PLANS.find(plan => plan.key === key) ?? PUBLIC_PLANS[0]
}

export function getPlanDisplayName(plan: string | null | undefined): string {
  if (plan === 'power') return 'Power (legacy)'
  const publicPlan = PUBLIC_PLANS.find(candidate => candidate.key === plan)
  return publicPlan?.name ?? 'Free'
}

export function hasPaidFeatureAccess(plan: string | null | undefined): boolean {
  return plan === 'pro' || plan === 'power' || plan === 'team'
}

export function getPlanPriceLabel(
  plan: Pick<PublicPlan, 'annual' | 'contact' | 'monthly' | 'perSeat'>,
  interval: BillingInterval,
): { price: string; period: string; monthlyEquivalent: string | null } {
  if (plan.contact) return { price: 'Let’s talk', period: '', monthlyEquivalent: null }
  if (plan.monthly === 0) return { price: '$0', period: 'forever', monthlyEquivalent: null }
  if (interval === 'year' && plan.annual !== null) {
    return {
      price: `$${plan.annual}`,
      period: plan.perSeat ? '/seat/year' : '/year',
      monthlyEquivalent: (plan.annual / 12).toFixed(2),
    }
  }
  return {
    price: `$${plan.monthly}`,
    period: plan.perSeat ? '/seat/month' : '/month',
    monthlyEquivalent: null,
  }
}

export interface RecordingChunkManifestEntry {
  chunkIndex: number
  startTimeMs: number
  endTimeMs: number
  mimeType: string
  r2Key?: string
  size?: number
  uploadedAt?: number
  /** See AudioRecording.encIv. Every stored object gets its own IV. */
  encIv?: string
}

export interface TranscriptSegment {
  text: string
  startTime: number
}

export interface AudioRecording {
  id: string
  dataUrl?: string
  r2Key?: string
  transcript: string
  segments: TranscriptSegment[]
  timestamp: number
  duration: number
  title: string
  x: number
  y: number
  width: number
  height: number
  /** Optional native Excalidraw frame that owns this spatial overlay. */
  frameId?: string | null
  /** Stable document ordering used by Note mode; spatial position is the legacy fallback. */
  readingOrderKey?: string
  transcribing?: boolean
  /** Capture lifecycle is checkpointed separately from transcription. */
  captureStatus?: 'recording' | 'complete' | 'interrupted'
  trimStart?: number
  trimEnd?: number
  /**
   * Canvas elements drawn while this recording was running, with their offset
   * in ms from the start of the audio. Powers "tap a stroke, hear that moment".
   */
  inkMarks?: { elementId: string; offsetMs: number }[]
  /** Moments the user flagged with the Mark button while recording. */
  bookmarks?: { offsetMs: number }[]
  chunkManifest?: RecordingChunkManifestEntry[]
  /**
   * Ordered byte slices of the complete audio file, used when the whole blob is
   * too large to upload (or to hold in a sync payload) in one piece.
   * Concatenating them in index order reproduces the original file exactly.
   */
  blobParts?: RecordingBlobPart[]
  /**
   * Base64 AES-GCM IV for the object at `r2Key`, present only on an encrypted
   * note's audio. Its presence is what marks the stored bytes as ciphertext:
   * playback decrypts with the note's key (the one its salt already derives)
   * before assembling the blob.
   *
   * The IV is per OBJECT, not per recording — chunks and blob parts carry their
   * own — because reusing an IV across encryptions under one key is exactly the
   * failure AES-GCM does not tolerate.
   */
  encIv?: string
  /**
   * The user opted (before recording) to keep only the transcript. The audio is
   * deleted once a complete final transcript has been saved.
   */
  discardAudioAfterTranscript?: boolean
  /** When the audio was deleted; the recording is transcript-only from then on. */
  audioDiscardedAt?: number
}

export interface RecordingBlobPart {
  index: number
  r2Key: string
  size: number
  mimeType?: string
  /** See AudioRecording.encIv. Every stored object gets its own IV. */
  encIv?: string
}

/**
 * Metadata for an original file retained alongside a note.
 *
 * The bytes are not embedded in note JSON. They live in the local IndexedDB
 * source-file store and, when cloud sync is available, under the note's R2
 * object prefix. This keeps sync payloads small while still supporting a
 * complete portable export.
 */
export interface SourceFileReference {
  id: string
  name: string
  mimeType: string
  size: number
  createdAt: number
  origin: 'apple-notes' | 'pdf-import' | 'share-target' | 'upload'
  r2Key?: string
}

export interface NoteSidecar {
  recordings?: AudioRecording[]
  noteCards?: unknown[]
  regions?: unknown[]
  imageScans?: unknown[]
  review?: NoteReviewState
  sourceFiles?: SourceFileReference[]
}

export interface NoteReviewState {
  version: 1
  createdAt: number
  reviewedAt?: number
  actionsConfirmedAt?: number
  takeawayConfirmedAt?: number
  completedAt?: number
}

export interface OmniCanvasFolder {
  id: string
  name: string
  parentId: string | null
  deletedAt?: number | null
  updatedAt?: number
  isEncrypted?: boolean
  encryptionSalt?: string
  isPinned?: boolean
}

export interface OmniCanvasNote {
  id: string
  title: string
  lastModified: number
  folderIds: string[]
  tags: string[]
  isPinned: boolean
  canvasData: unknown
  stickyNotes?: unknown[]
  markdownBlocks?: unknown[]
  recordings?: AudioRecording[]
  noteCards?: unknown[]
  regions?: unknown[]
  ocrText?: string
  imageScans?: unknown[]
  review?: NoteReviewState
  sourceFiles?: SourceFileReference[]
  deletedAt?: number | null
  isEncrypted?: boolean
  encryptionSalt?: string
  encryptionIV?: string | null
  encryptionSource?: EncryptionSource
  paperStyle?: string
  /** Local-only: true when note has changes that have not been accepted by the server. */
  _dirty?: boolean
  /** Local-only: timestamp of the last server-accepted write for this note. */
  _lastSyncedAt?: number
  /**
   * Local-only: the `lastModified` value the server held the last time this
   * device and the server agreed on the note (after a push it accepted, or a
   * pull it applied). A pulled row carrying exactly this value is the version
   * our local edits were made on top of, so a dirty local copy is strictly
   * newer and must win without a conflict copy. Comparing device clocks to the
   * server clock could not tell that apart from a real conflict.
   */
  _syncedLastModified?: number
}

export interface SyncPullRemoteNote {
  id: string
  title: string
  is_pinned: number | boolean
  canvas_data: string | null
  sticky_notes: string | null
  markdown_blocks?: string | null
  sidecar_data?: string | null
  ocr_text?: string | null
  deleted_at?: number | null
  last_modified: number
  updated_at: number
  is_encrypted?: number | boolean | null
  encryption_salt?: string | null
  encryption_iv?: string | null
  encryption_source?: EncryptionSource | null
  paper_style?: string | null
  content_hash?: string | null
}

export interface SyncPullResult {
  notes: SyncPullRemoteNote[]
  folders: SyncPullRemoteFolder[]
  noteFolders: unknown[]
  noteTags: unknown[]
  serverTime: number
  totalNotes?: number
}

export interface SyncPullRemoteFolder {
  id: string
  name: string
  parent_id: string | null
  deleted_at?: number | null
  updated_at?: number
  is_encrypted?: number | boolean | null
  encryption_salt?: string | null
}

export interface SyncPushResult {
  serverTime: number
  /** Note IDs whose writes were accepted by the server and can clear local dirty state. */
  updatedNoteIds: string[]
  /** Note IDs whose writes were rejected, usually because the server has a newer last_modified. */
  rejectedNoteIds: string[]
  /**
   * Note IDs held back locally for exceeding the per-note push ceiling. They
   * stay dirty and are never sent, so one oversized note cannot fail the batch
   * it travels in.
   */
  oversizedNoteIds?: string[]
}

export const AI_ROUTES = {
  summarize: '/api/summarize',
  summarizePerspectives: '/api/summarize-perspectives',
  chatTranscript: '/api/chat-transcript',
  coach: '/api/coach',
  analyzeSentiment: '/api/analyze-sentiment',
  extractEntities: '/api/extract-entities',
  diarize: '/api/diarize',
  generateMindMap: '/api/generate-mindmap',
  generateMindMapNotes: '/api/generate-mindmap-notes',
  autoLabel: '/api/auto-label',
  organizeTitles: '/api/organize/titles',
  organizeFolders: '/api/organize/folders',
  organizeAssign: '/api/organize/assign',
} as const

export type AiRouteName = keyof typeof AI_ROUTES
export type AiRoutePath = (typeof AI_ROUTES)[AiRouteName]

export const SYNC_ROUTES = {
  pull: '/api/sync/pull',
  push: '/api/sync/push',
  // Hard delete. Sync push can only ever soft-delete, because a purged note is
  // one the client has stopped tracking and so will never mention again.
  purge: '/api/sync/purge',
} as const

export type SyncRouteName = keyof typeof SYNC_ROUTES
export type SyncRoutePath = (typeof SYNC_ROUTES)[SyncRouteName]

export const BILLING_ROUTES = {
  checkout: '/api/checkout',
  portal: '/api/billing-portal',
  webhook: '/api/stripe/webhook',
} as const

export const FILE_ROUTES = {
  upload: '/api/files/upload',
  base: '/api/files/',
  storageQuota: '/api/storage/quota',
} as const

export const UTILITY_ROUTES = {
  canvasStash: '/api/canvas-stash',
  mobileWaitlist: '/api/mobile-waitlist',
  proxyLibrary: '/api/proxy-library',
  downloadsBase: '/api/downloads/',
  notarizeStatus: '/api/notarize-status',
  aiHealth: '/api/ai-health',
  healthz: '/api/healthz',
  healthcheck: '/api/healthcheck',
  health: '/api/health',
  deepHealth: '/api/deep-health',
} as const

export const RESEARCH_ROUTES = {
  switchSurvey: '/api/switch-survey',
  feedback: '/api/feedback',
} as const

export const VERSION_STATS_ROUTE = '/api/version-stats'

export const FEEDBACK_KINDS = ['bug', 'idea', 'confusing', 'praise'] as const
export type FeedbackKind = (typeof FEEDBACK_KINDS)[number]

export const LIBRARY_ROUTES = {
  library: '/api/library',
  vocabulary: '/api/vocabulary',
  vocabularyBase: '/api/vocabulary/',
} as const

export const NOTIFICATION_ROUTES = {
  preferences: '/api/notifications/preferences',
  unsubscribe: '/api/notifications/unsubscribe',
} as const

export const MEDIA_ROUTES = {
  transcribe: '/api/transcribe',
  transcribeRecording: '/api/transcribe/recording',
  ocr: '/api/ocr',
  scanImage: '/api/scan-image',
  recordingRecovery: '/api/recordings/recovery',
  youtubeQuota: '/api/youtube/quota',
  youtubeTranscript: '/api/youtube/transcript',
  clip: '/api/clip',
} as const

export const SEARCH_ROUTES = {
  semanticSearch: '/api/search',
  relatedNotes: '/api/search/related',
  reindexVectors: '/api/admin/reindex-vectors',
} as const

/**
 * bge-base puts almost any two English documents above 0.6 cosine, so a raw score reads as
 * "everything is related to everything". Rescaling from that floor turns it back into a usable
 * 0..1 signal; anything below it carries no information at all.
 *
 * Lives here rather than in the worker because both sides need the same scale: the worker
 * normalises, and the graph's link suggestions weigh the result against TF-IDF.
 */
export const EMBEDDING_SIMILARITY_FLOOR = 0.62

export function normaliseEmbeddingScore(score: number): number {
  if (!Number.isFinite(score) || score <= EMBEDDING_SIMILARITY_FLOOR) return 0
  return Math.min(1, (score - EMBEDDING_SIMILARITY_FLOOR) / (1 - EMBEDDING_SIMILARITY_FLOOR))
}

export const COLLAB_ROUTES = {
  inviteEmail: '/api/collab/invite-email',
  join: '/api/collab/join',
  noteBase: '/api/collab/note/',
  connect: '/api/collab/connect',
  userChannelConnect: '/api/user/connect',
} as const

// Anonymous, throwaway collaboration rooms minted from the public marketing
// demo. Deliberately separate from COLLAB_ROUTES: those all hang off a note the
// caller owns, and a demo visitor has neither a note nor an account.
export const DEMO_ROOM_ROUTES = {
  create: '/api/demo-room',
  join: '/api/demo-room/join',
} as const

export const AUTH_ROUTES = {
  signup: '/api/auth/signup',
  login: '/api/auth/login',
  me: '/api/auth/me',
  forgotPassword: '/api/auth/forgot-password',
  resetPassword: '/api/auth/reset-password',
  googlePoll: '/api/auth/google/poll',
  googleStart: '/api/auth/google/start',
  googleCallback: '/api/auth/google/callback',
  googleExchange: '/api/auth/google/exchange',
  mfaVerify: '/api/auth/mfa/verify',
  mfaSetup: '/api/auth/mfa/setup',
  mfaEnable: '/api/auth/mfa/enable',
  mfaDisable: '/api/auth/mfa/disable',
  mfaSkipGoogle: '/api/auth/mfa/skip-google',
  changePassword: '/api/auth/change-password',
  disablePassword: '/api/auth/disable-password',
} as const

export function noteInvitesRoute(noteId: string): string {
  return `/api/notes/${noteId}/invites`
}

export function noteInviteRoute(noteId: string, token: string): string {
  return `/api/notes/${noteId}/invites/${token}`
}

export function noteVersionsRoute(noteId: string, recovery = false): string {
  return `/api/notes/${noteId}/versions${recovery ? '?recovery=1' : ''}`
}

export function noteVersionRoute(noteId: string, versionId: string, recovery = false): string {
  return `/api/notes/${noteId}/versions/${versionId}${recovery ? '?recovery=1' : ''}`
}

export function noteVersionRestoreRoute(noteId: string, versionId: string, recovery = false): string {
  return `/api/notes/${noteId}/versions/${versionId}/restore${recovery ? '?recovery=1' : ''}`
}

export function collabNoteRoute(noteId: string): string {
  return `${COLLAB_ROUTES.noteBase}${noteId}`
}

export const TEAM_ROUTES = {
  team: '/api/team',
  teamBase: '/api/team/',
} as const

export function teamRoute(teamId: string): string {
  return `${TEAM_ROUTES.teamBase}${teamId}`
}

export function teamMembersRoute(teamId: string): string {
  return `${TEAM_ROUTES.teamBase}${teamId}/members`
}

export function teamMemberRoute(teamId: string, userId: string): string {
  return `${TEAM_ROUTES.teamBase}${teamId}/members/${userId}`
}

export function teamInviteRoute(teamId: string): string {
  return `${TEAM_ROUTES.teamBase}${teamId}/invite`
}

export const BACKUP_ROUTES = {
  export: '/api/backup/export',
  import: '/api/backup/import',
} as const

export type BackupRouteName = keyof typeof BACKUP_ROUTES
export type BackupRoutePath = (typeof BACKUP_ROUTES)[BackupRouteName]

// The same two operations under the API-key surface, for scripted/cron backups.
export const BACKUP_API_ROUTES = {
  export: '/api/v1/backup/export',
  import: '/api/v1/backup/import',
} as const

// --- Canvas element merge ---------------------------------------------------

/**
 * Element-level merge for two Excalidraw element lists: higher `version` wins,
 * ties broken by higher `versionNonce`. Used by the collab client and the
 * CanvasRoom Durable Object; both MUST use this one so a room and its clients
 * can never disagree about which element wins.
 */
export function mergeElementsByVersion<T extends { id: string; version: number; versionNonce: number }>(
  current: readonly T[],
  incoming: readonly T[],
): T[] {
  const map = new Map<string, T>()
  for (const element of current) map.set(element.id, element)
  for (const element of incoming) {
    const existing = map.get(element.id)
    if (!existing || element.version > existing.version ||
        (element.version === existing.version && element.versionNonce > existing.versionNonce)) {
      map.set(element.id, element)
    }
  }
  return Array.from(map.values())
}
